/**
 * The WhatsApp hand-off for a customer's assessment QUESTIONS — pure functions,
 * so the dialog's preview and the link it sends are guaranteed to be the same
 * text. Questions only: the record holds nothing about what was said, by
 * decision (see `CustomerAssessmentDto`), and this module could not send an
 * answer even if one were wanted. The work-history employer is shown beside the
 * message in the dialog, not inside it.
 *
 * Sending goes through WhatsApp's own click-to-chat link (`wa.me`): it opens a
 * chat with the customer's number, message pre-filled, in the WhatsApp app on a
 * phone and in WhatsApp Web/Desktop on a computer, and the operator presses
 * send. There is no server-side WhatsApp integration in this system and this
 * route needs none — the message goes out from the operator's own WhatsApp,
 * which is the number the customer already knows.
 *
 * The wording is byte-for-byte the sibling agent panel's, on purpose: a
 * customer may be handed the same questions from either console, and two
 * differently worded messages about one assessment read as two assessments.
 */

import type { CustomerAssessmentDto, CustomerAssessmentQuestionDto } from '@acs/shared'

/* ------------------------------------------------------------------ *
 * The number
 * ------------------------------------------------------------------ */

/**
 * The customer's number as `wa.me` wants it: country code + number, digits
 * only, no plus. This console stores Canadian numbers, usually as bare ten
 * digits, sometimes with a leading `1` or `+1`. `null` when the shape is not
 * something WhatsApp could route — the caller then offers copy instead of a
 * link that opens a chat with nobody.
 */
export function whatsappDigits(phone: string | null | undefined): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return digits
  // Some other international shape — pass it through if it is plausible
  // (E.164 allows up to 15 digits; nothing routable is shorter than 8).
  if (digits.length >= 8 && digits.length <= 15) return digits
  return null
}

export function whatsappHref(digits: string, text: string): string {
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
}

/* ------------------------------------------------------------------ *
 * The clock
 * ------------------------------------------------------------------ */

/**
 * The assessments happen in Toronto, so that is the clock shown — fixed, no
 * matter where the operator or the customer is. Every other timestamp in this
 * console is in the viewer's zone; this one goes INTO a message to the
 * customer, whose clock is not the operator's.
 */
const ASSESSMENT_TIME_ZONE = 'America/Toronto'

const WHEN_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: ASSESSMENT_TIME_ZONE,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZoneName: 'short',
})

/**
 * "Aug 31, 2026, 3:42 PM EDT" — date AND time, always Toronto time (the zone
 * name says so). `null` for bad or missing data. Shared by the message body
 * and the dialog header so the two can never disagree.
 */
export function assessmentWhenLabel(iso: string | null | undefined): string | null {
  if (!iso) return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  return WHEN_FORMAT.format(ms)
}

/* ------------------------------------------------------------------ *
 * The questions
 * ------------------------------------------------------------------ */

/**
 * What the customer needs handed back, in the order it was asked.
 *
 * Evaluation questions only — the sound check is a microphone test, not a
 * question — and only those with some text to send: the clip transcript IS the
 * question as far as the customer is concerned, and the on-screen instruction
 * ("Please listen to… and summarise…") is the fallback for a record whose
 * transcript was never captured, so an item can never go out empty.
 */
export function sendableQuestions(
  questions: readonly CustomerAssessmentQuestionDto[]
): { order: number; body: string }[] {
  return questions
    .filter((q) => q.kind === 'evaluation')
    .map((q) => ({ order: q.order, body: clean(q.situationText ?? q.questionText) }))
    .filter((q) => q.body !== '')
    .sort((a, b) => a.order - b.order)
}

/** WhatsApp keeps newlines, but stray CRs and runs of blank lines read badly. */
function clean(s: string | null | undefined): string {
  if (!s) return ''
  return s
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/* ------------------------------------------------------------------ *
 * The message
 * ------------------------------------------------------------------ */

/**
 * The message itself: the questions, nothing else. Just "Question N" and the
 * text — no Amazon part titles ("Description of the work situation"), no
 * module names: the customer only needs what was asked. WhatsApp's own light
 * markup (`*bold*`) keeps the sections scannable on a phone; the dialog
 * renders that markup the way WhatsApp will, so the preview is what they see.
 */
export function buildAssessmentMessage(
  customerName: string,
  assessment: Pick<CustomerAssessmentDto, 'questions' | 'startedAt' | 'endedAt'>
): string {
  const firstName = customerName.trim().split(/\s+/)[0] || 'there'
  const when = assessmentWhenLabel(assessment.endedAt ?? assessment.startedAt)
  const questions = sendableQuestions(assessment.questions)

  const lines: string[] = []
  lines.push(`Hi ${firstName} 👋`)
  lines.push('')
  lines.push(
    `Here are the questions from your Amazon assessment${when ? ` (${when})` : ''}, for your records.`
  )

  questions.forEach((q, i) => {
    lines.push('')
    lines.push(`*Question ${i + 1}:*`)
    lines.push(q.body)
  })

  if (questions.length === 0) {
    lines.push('')
    lines.push('No spoken questions were recorded for this assessment.')
  }

  lines.push('')
  lines.push(
    'Please keep this handy in case Amazon asks about it. Reply here if you have any questions.'
  )

  return lines.join('\n')
}

/* ------------------------------------------------------------------ *
 * The preview
 * ------------------------------------------------------------------ */

export type MessageLine =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'blank' }

/**
 * The message, line by line, with WhatsApp's `*bold*` resolved — so the
 * preview can render it the way the customer's phone will, from the SAME
 * string that is copied and sent. Only whole-line bold is recognised, which is
 * the only markup `buildAssessmentMessage` emits; anything else is text.
 */
export function messageLines(text: string): MessageLine[] {
  return text.split('\n').map((line): MessageLine => {
    if (line === '') return { kind: 'blank' }
    const bold = /^\*([^*]+)\*$/.exec(line)
    return bold ? { kind: 'bold', text: bold[1] ?? '' } : { kind: 'text', text: line }
  })
}
